import './menu-view-all.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { IName } from '../../../interfaces/global.interfaces';
import { Data } from '../../../models/data';
import { IDataCoupon, IDataItem } from '../../../models/data.interface';
import { MenuItemViewAllCard } from '../../shared/MenuItemViewAllCard/menu-item-view-all-card';
import { Page } from '../page';
import { Order } from '../../../models/order';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { DataLayerService } from '../../../services/data-layer-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { ProfileService } from '../../../services/profile-service';
import { Modals, Pages } from '../../../types/enums';
import { ICustomizeItemProps } from '../../modals/CustomizeItem/customize-item.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { MealDealItemCard } from '../../shared/MealDealItemCard/meal-deal-item-card';

/**
 * Class representing the Landing page.
 */
export class MenuViewAll extends Page {
    private readonly _order: Order;
    private readonly _dataLayer: DataLayerService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private _categoryId: number = 0;
    private _subcategoryId: number | null = null;
    private readonly _profileService: ProfileService;
    private _mealDealCoupons: IDataCoupon[] = [];

    /**
     * Creates an instance of the Landing page.
     * @constructor
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider, profileService: ProfileService) {
        super("page_menu_all", eventBus, data, context, localizationService);
        this._order = order;
        this._dataLayer = dataLayer;
        this._loyaltyProvider = loyaltyProvider;
        this._eventBusListeners();
        this._profileService = profileService;
    }

    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages, _: boolean, categoryId: number, subcategoryId: number | null, mealDealCoupons: IDataCoupon[]) => {
            this._displayData(page, categoryId, subcategoryId, mealDealCoupons);
        });
    }

    public initAsync = async (): Promise<void> => {

    }

    public _displayData = async (page: Pages, categoryId: number, subcategoryId: number | null, mealDealCoupons: IDataCoupon[]): Promise<void> => {
        if (page !== Pages.MENU_VIEW_ALL)
            return;

        this._categoryId = categoryId;
        this._subcategoryId = subcategoryId;
        this._mealDealCoupons = mealDealCoupons;
        this._renderPage();

        if (this._dataLayer && this._data.storeKey)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.getProperty("StoreInfo").storeCode.toString(), this._profileService.user.user);
    }

    private readonly _renderPage = (): void => {
        const parentDiv = document.getElementById("div_menu_view_all");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        if (!this._subcategoryId) {
            const category = this._data.getProperty("Categories")?.find(x => x.ItemWebCategoryId === this._categoryId);
            this._setHeaderName(category?.Names || []);

            if (category?.IsMealDeal) {
                this._createMealDealViewAllCards(parentDiv);
                return;
            }

            const categoryItems = this._data.getProperty("Items")?.filter(x => x.WebCategoryId === this._categoryId && x.SubCategoryId === 0) || [];
            this._createMenuItemViewAllCards(parentDiv, categoryItems);
        } else {
            const subcategory = this._data.getProperty("SubCategories")?.find(subCat => subCat.ItemSubCategoryId === this._subcategoryId);
            this._setHeaderName(subcategory?.Names || []);

            const subCategoryItems = this._data.getProperty("Items")?.filter(x => x.SubCategoryId === this._subcategoryId) || [];
            this._createMenuItemViewAllCards(parentDiv, subCategoryItems);
        }
    }

    private readonly _createMenuItemViewAllCards = (parentDiv: HTMLElement, items: IDataItem[]): void => {
        for (const [index, item] of items.entries()) {
            const card = new MenuItemViewAllCard(this._localizationService, item, () => { this._menuItemCardOnClick(item, 1, index); });
            parentDiv.appendChild(card.element);
        }
    }

    private readonly _createMealDealViewAllCards = (parentDiv: HTMLElement): void => {
        const mealDealCoupons = this._mealDealCoupons;

        let index = 0;
        for (const mealDealCoupon of mealDealCoupons) {
            const card = new MealDealItemCard(this._data, this._order, this._localizationService, this._loyaltyProvider, this._dataLayer, mealDealCoupon, index);
            parentDiv.appendChild(card.element);
            index++;
        }
    }

    private readonly _menuItemCardOnClick = (mItem: IDataItem, quantity: number, index: number): void => {
        const defaultSize = OnlineOrderingUtil.GetItemDefaultSize(mItem, this._order.getProperty("OrderTypeID") || 0, null, this._data.getProperty("Settings")?.DSZID || 0);

        if (this._dataLayer) {
            const orderItem = this._order._createOrderItem(mItem, defaultSize.SizeId, 0, quantity, []);
            this._dataLayer.select_item(this._order, orderItem, index, Pages.MENU.replace('/', ''));
        }

        OnlineOrderingUtil.checkAgeAndCustomizeItem(this._context, this._localizationService, mItem, () => {
            const props: ICustomizeItemProps = {
                sizeId: defaultSize.SizeId,
                quantity: quantity,
                mealDeal: null,
                recentOrder: false,
                mItem: mItem,
                itemsToEdit: []
            };

            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
        });
    }

    private readonly _setHeaderName = (names: IName[]): void => {
        const name = this._localizationService.GetName(names);
        const data = { name: name, ltagj: Util.toLtagj(names) };
        this._eventBus.emit(EventTypes.HEADER_NAME_CHANGE, data);
    }
}